import { getCookie, clearAllCookies } from "../../services/Cookies";
import { refreshAccessToken } from "./refreshAuth";

const BASE_URL = process.env.REACT_APP_API_URL ?? "";

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    super(`API ${status}: ${statusText}`);
    this.name = "ApiError";
  }
}

function getAuthHeaders(): Record<string, string> {
  const accessToken = getCookie("accessToken");
  const tokenType = getCookie("tokenType");
  if (accessToken && tokenType) {
    return { Authorization: `${tokenType} ${accessToken}` };
  }
  return {};
}

function redirectToLogin(): never {
  clearAllCookies();
  window.location.href = "/login";
  throw new ApiError(401, "Unauthorized", null);
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 403) {
    throw new ApiError(403, "Forbidden", null);
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // ignore parse errors for error bodies
    }
    throw new ApiError(response.status, response.statusText, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

async function rawFetch(
  method: string,
  path: string,
  body?: unknown,
): Promise<Response> {
  const headers: Record<string, string> = {
    accept: "application/json",
    ...getAuthHeaders(),
  };
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  return fetch(`${BASE_URL}${path}`, init);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let response = await rawFetch(method, path, body);
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) redirectToLogin();
    response = await rawFetch(method, path, body);
    if (response.status === 401) redirectToLogin();
  }
  return handleResponse<T>(response);
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, body);
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PUT", path, body);
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>("DELETE", path);
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PATCH", path, body);
}

async function rawFetchWithRefresh(
  method: string,
  path: string,
  body?: unknown,
): Promise<Response> {
  const buildInit = (): RequestInit => {
    const headers: Record<string, string> = {
      accept: "application/json",
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    };
    return {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };
  };
  let response = await fetch(`${BASE_URL}${path}`, buildInit());
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) return response;
    response = await fetch(`${BASE_URL}${path}`, buildInit());
  }
  return response;
}

export function apiRawGet(path: string): Promise<Response> {
  return rawFetchWithRefresh("GET", path);
}

export function apiRawPost(
  path: string,
  body?: unknown,
): Promise<Response> {
  return rawFetchWithRefresh("POST", path, body);
}

export { ApiError };
